import { unstable_editorExperimentParam } from '@atlaskit/tmp-editor-statsig/experiments';

import { createRequestGenerator } from './generator';
import type { TriggerContext, ImproveWritingDetails } from './types';

export async function canNudgeFullPageImproveWriting({
	proactiveAIApiUrl,
	product,
	context,
}: {
	proactiveAIApiUrl: string;
	product: string;
	context: TriggerContext;
}) {
	const requestGenerator = createRequestGenerator({
		endpoint: proactiveAIApiUrl,
		product,
		context,
	});
	let canNudge = false;
	let nudgeMetrics = {
		finalConfidence: undefined as number | undefined,
		alternativeConfidence: undefined as number | undefined,
		lengthPercentageDifference: undefined as number | undefined,
		inputReadabilityScore: undefined as number | undefined,
		outputReadabilityScore: undefined as number | undefined,
	};

	if (!requestGenerator) {
		return {
			canNudge,
			nudgeMetrics,
		};
	}

	for await (const responseState of requestGenerator) {
		switch (responseState.state) {
			case 'cached':
			case 'parsed': {
				const replaceParagraphTransform = responseState.recommendations.find(
					(r) => r.transformType === 'REPLACE_PAGE' && r.type === 'IMPROVE_WRITING',
				);

				if (
					!replaceParagraphTransform ||
					!replaceParagraphTransform.details ||
					replaceParagraphTransform.type !== 'IMPROVE_WRITING'
				) {
					break;
				}

				canNudge = checkNudgeCriteria(replaceParagraphTransform.details);

				nudgeMetrics = {
					finalConfidence: replaceParagraphTransform.details.final_confidence,
					alternativeConfidence: replaceParagraphTransform.details.alternative_confidence,
					lengthPercentageDifference:
						replaceParagraphTransform.details.length_percentage_difference,
					inputReadabilityScore: replaceParagraphTransform.details.input_readability_score,
					outputReadabilityScore: replaceParagraphTransform.details.output_readability_score,
				};

				break;
			}
		}
	}

	return {
		canNudge,
		nudgeMetrics,
	};
}

/**
 * Checks if the improve writing recommendation meet all the nudge criteria.
 */
const checkNudgeCriteria = (details: ImproveWritingDetails): boolean => {
	const minimalFinalScore = unstable_editorExperimentParam(
		'platform_editor_ai_proactive_ai_nudge_parameters',
		'min_final_confidence',
		{
			defaultValue: -Infinity,
			typeGuard: (value: unknown): value is number => typeof value === 'number',
		},
	);

	const minLengthPercentageDifference = unstable_editorExperimentParam(
		'platform_editor_ai_proactive_ai_nudge_parameters',
		'min_length_percentage_difference',
		{
			defaultValue: -Infinity,
			typeGuard: (value: unknown): value is number => typeof value === 'number',
		},
	);
	const maxLengthPercentageDifference = unstable_editorExperimentParam(
		'platform_editor_ai_proactive_ai_nudge_parameters',
		'max_length_percentage_difference',
		{
			defaultValue: Infinity,
			typeGuard: (value: unknown): value is number => typeof value === 'number',
		},
	);

	const minInputReadabilityScore = unstable_editorExperimentParam(
		'platform_editor_ai_proactive_ai_nudge_parameters',
		'min_input_readability_score',
		{
			defaultValue: -Infinity,
			typeGuard: (value: unknown): value is number => typeof value === 'number',
		},
	);

	const minOutputReadabilityScore = unstable_editorExperimentParam(
		'platform_editor_ai_proactive_ai_nudge_parameters',
		'min_output_readability_score',
		{
			defaultValue: -Infinity,
			typeGuard: (value: unknown): value is number => typeof value === 'number',
		},
	);

	const maxReadabilityScoreDelta = unstable_editorExperimentParam(
		'platform_editor_ai_proactive_ai_nudge_parameters',
		'max_readability_score_delta',
		{
			defaultValue: Infinity,
			typeGuard: (value: unknown): value is number => typeof value === 'number',
		},
	);

	const minReadabilityScoreDelta = unstable_editorExperimentParam(
		'platform_editor_ai_proactive_ai_nudge_parameters',
		'min_readability_score_delta',
		{
			defaultValue: -Infinity,
			typeGuard: (value: unknown): value is number => typeof value === 'number',
		},
	);

	const minAlternativeConfidence = unstable_editorExperimentParam(
		'platform_editor_ai_proactive_ai_nudge_parameters',
		'min_alternative_confidence',
		{
			defaultValue: -Infinity,
			typeGuard: (value: unknown): value is number => typeof value === 'number',
		},
	);

	// Check final confidence score
	const meetsFinalScoreCriteria =
		details.final_confidence === undefined || details.final_confidence >= minimalFinalScore;

	// Check min length percentage difference
	const meetsMinLengthCriteria =
		details.length_percentage_difference === undefined ||
		details.length_percentage_difference >= minLengthPercentageDifference;

	// Check max length percentage difference
	const meetsMaxLengthCriteria =
		details.length_percentage_difference === undefined ||
		details.length_percentage_difference <= maxLengthPercentageDifference;

	// Check input readability score
	const meetsInputReadabilityCriteria =
		details.input_readability_score === undefined ||
		details.input_readability_score >= minInputReadabilityScore;

	// Check output readability score
	const meetsOutputReadabilityCriteria =
		details.output_readability_score === undefined ||
		details.output_readability_score >= minOutputReadabilityScore;

	// Check readability score delta
	const meetsReadabilityDeltaCriteria =
		details.output_readability_score === undefined ||
		details.input_readability_score === undefined ||
		details.output_readability_score - details.input_readability_score <= maxReadabilityScoreDelta;

	// Check minimum readability score improvement
	const meetsMinReadabilityDeltaCriteria =
		details.output_readability_score === undefined ||
		details.input_readability_score === undefined ||
		details.output_readability_score - details.input_readability_score >= minReadabilityScoreDelta;

	// Check alternative confidence
	const meetsAlternativeConfidenceCriteria =
		details.alternative_confidence === undefined ||
		details.alternative_confidence >= minAlternativeConfidence;

	// All criteria must pass
	return (
		meetsFinalScoreCriteria &&
		meetsMinLengthCriteria &&
		meetsMaxLengthCriteria &&
		meetsInputReadabilityCriteria &&
		meetsOutputReadabilityCriteria &&
		meetsReadabilityDeltaCriteria &&
		meetsMinReadabilityDeltaCriteria &&
		meetsAlternativeConfidenceCriteria
	);
};
